import React, { useEffect, useMemo, useRef, useState } from "react";
import { Helmet } from "react-helmet";
import { Link } from "react-router-dom";
import NextGenPlayer from "../../video-player/Components/NextGenPlayer";
import { createAvodPlugin } from "../../video-player/Plugins/builtins/avodPlugin";
import { createSubtitlesPlugin } from "../../video-player/Plugins/builtins/subtitlesPlugin";
import {
  siteBrand,
  siteFeatures,
} from "../../video-player/production/buildHostPlayerConfig";
import {
  trackBroadcastEvent,
  toggleFavoriteChannel,
  getFavoriteChannelIds,
  pushRecentChannel,
  fetchEpg,
} from "../Broadcast/channelScheduleApi";
import { controlPlaneFromChannelNow } from "../Broadcast/controlPlaneFromChannelNow";
import {
  resolveChannelStreamUrl,
  safeCssBackgroundUrl,
  collectChannelStreamCandidates,
} from "../Broadcast/channelPlaybackHelpers";
import { useChannelNow } from "../Broadcast/useChannelNow";
import { fetchContentShow } from "./contentApi";
import {
  computeGuideTimeline,
  isOpenEndedProgram,
  programSegmentGeometry,
  isSameGuideProgram,
} from "./guideTimeline";
import {
  followSelectedProgram,
  resolveProgramPanelMeta,
  airingVideoMatchesPlayingMedia,
  matchEpgChannelRow,
  resolveChannelGuidePrograms,
  resolveChannelHeaderMeta,
} from "./channelProgramPanel";
import { resolveVideoContextualActions } from "../Helper/videoContextualActions";
import api from "../../Environment";
import ErrorComponent from "../Static/Error";
import "./ChannelDetailPage.css";

function programKey(p) {
  if (!p) return "";
  return String(
    p.id ||
      p.unique_id ||
      p.schedule_entry_id ||
      (p.title || "") + ":" + (p.starts_at || "")
  );
}

function formatClock(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleTimeString([], {
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch (e) {
    return "";
  }
}

function nextProgramStartMs(programs, index) {
  for (var i = index + 1; i < programs.length; i++) {
    var n = programs[i];
    if (n && n.starts_at) {
      var t = new Date(n.starts_at).getTime();
      if (isFinite(t)) return t;
    }
  }
  return null;
}

/**
 * Consumer Channel Detail — Live TV / Guide: player + horizontal EPG.
 * Auto-tunes when now-playing resolves; WATCH LIVE opens TPG Player fullscreen.
 */
function ChannelDetailPage(props) {
  const channelKey =
    (props.match && props.match.params && props.match.params.slug) || "";

  const [detail, setDetail] = useState(null);
  const [schedule, setSchedule] = useState([]);
  const [detailError, setDetailError] = useState("");
  const [notFound, setNotFound] = useState(false);
  const [watching, setWatching] = useState(false);
  const [tunedOnce, setTunedOnce] = useState(false);
  const [playerError, setPlayerError] = useState(null);
  const [failoverIndex, setFailoverIndex] = useState(0);
  const [favorites, setFavorites] = useState(() => getFavoriteChannelIds());
  const [airingVideo, setAiringVideo] = useState(null);
  const [selectedProgram, setSelectedProgram] = useState(null);
  const [guideWindow, setGuideWindow] = useState(null);
  const playerApiRef = useRef(null);
  const userPickedProgramRef = useRef(false);

  const {
    nowPayload,
    error: nowError,
    loading: nowLoading,
    refresh: refreshNow,
  } = useChannelNow(channelKey);

  useEffect(() => {
    setWatching(false);
    setTunedOnce(false);
    setPlayerError(null);
    setFailoverIndex(0);
    setDetail(null);
    setSchedule([]);
    setAiringVideo(null);
    setSelectedProgram(null);
    userPickedProgramRef.current = false;
    setNotFound(false);
    setDetailError("");
    playerApiRef.current = null;
  }, [channelKey]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setDetailError("");
      setNotFound(false);
      try {
        const show = await fetchContentShow({
          slug: channelKey,
          unique_id: channelKey,
        });
        if (cancelled) return;
        if (show && show.success && show.data) {
          setDetail(show.data);
        } else {
          // Soft miss — scheduler channels may lack a ContentItem; now-playing is SSOT.
          // Never surface content/show miss as a channel page error banner.
          setDetail(null);
        }
      } catch (e) {
        // Defense in depth: content/show is optional enrichment. Axios 404 / status
        // messages must never become the channel-detail error banner.
        const status = e && e.response && e.response.status;
        const msg = String((e && e.message) || "");
        const soft =
          status === 404 ||
          /status code 404/i.test(msg) ||
          /not found/i.test(msg);
        if (!cancelled) {
          setDetail(null);
          if (!soft) {
            setDetailError(msg || "Failed to load channel detail");
          }
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [channelKey]);

  // Invalid / unpublished slug must not silently resolve to another channel.
  useEffect(() => {
    if (!channelKey || nowLoading) return;
    if (nowPayload && nowPayload.success === false) {
      const err = String(nowPayload.error || "");
      if (
        err === "channel_not_found" ||
        err === "channel_not_available" ||
        err.indexOf("not_found") >= 0
      ) {
        setNotFound(true);
      }
    }
    if (nowPayload && nowPayload.success !== false && nowPayload.channel) {
      setNotFound(false);
    }
  }, [nowPayload, nowLoading, channelKey]);

  // When current/selected program is linked to a VOD asset, reuse videos/view associations
  // for View Organization / View Creator (same as VOD details — not Scheduler links).
  // Drop stale videos/view payloads when the target admin_video_id changes (race lock).
  useEffect(() => {
    let cancelled = false;
    const program = selectedProgram || (nowPayload && nowPayload.current) || {};
    const media = (nowPayload && nowPayload.media_asset) || {};
    const meta = (media && media.metadata) || {};
    const viewingCurrent = isSameGuideProgram(
      program,
      nowPayload && nowPayload.current
    );
    const adminVideoId =
      program.admin_video_id ||
      (viewingCurrent
        ? meta.admin_video_id || meta.resolved_admin_video_id || null
        : null) ||
      null;
    const targetId = adminVideoId != null ? String(adminVideoId) : "";
    setAiringVideo((prev) => {
      if (!targetId) return null;
      if (!prev) return null;
      const prevId = String(prev.admin_video_id || prev.id || "");
      return prevId && prevId === targetId ? prev : null;
    });
    if (!adminVideoId) {
      return undefined;
    }
    (async () => {
      try {
        const response = await api.postMethod("videos/view", {
          admin_video_id: adminVideoId,
        });
        if (
          !cancelled &&
          response &&
          response.data &&
          response.data.success &&
          response.data.data
        ) {
          setAiringVideo(response.data.data);
        } else if (!cancelled) {
          setAiringVideo(null);
        }
      } catch (e) {
        if (!cancelled) setAiringVideo(null);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [nowPayload, selectedProgram]);

  // Auto-start watching once now-playing resolves (live player present — not a second hop).
  useEffect(() => {
    if (!nowPayload || nowPayload.success === false || tunedOnce) return;
    const ch = nowPayload.channel || {};
    pushRecentChannel(ch.id || ch.unique_id || channelKey);
    trackBroadcastEvent({
      event_type: "tune",
      channel_id: ch.id,
      media_asset_key: nowPayload.media_asset && nowPayload.media_asset.id,
      schedule_entry_uid: nowPayload.current && nowPayload.current.unique_id,
    });
    setWatching(true);
    setTunedOnce(true);
  }, [nowPayload, channelKey, tunedOnce]);

  // Keep selected program aligned with /channels/now unless user picked a Guide segment.
  useEffect(() => {
    if (!nowPayload || nowPayload.success === false) return;
    const current = nowPayload.current;
    if (!current) return;
    setSelectedProgram((prev) => {
      const next = followSelectedProgram(
        prev,
        current,
        userPickedProgramRef.current
      );
      if (next.clearUserPick) {
        userPickedProgramRef.current = false;
      }
      return next.program;
    });
  }, [nowPayload]);

  // Public EPG via channels/epg (same published path as /guide) with UTC ISO window.
  // Not ops-only channels/timeline. Seed from now/next when the window is empty.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const ch = (nowPayload && nowPayload.channel) || {};
      const id = ch.id || ch.unique_id || channelKey;
      if (!id) return;
      const from = new Date();
      from.setMinutes(0, 0, 0);
      const to = new Date(from.getTime() + 24 * 3600000);
      const fromIso = from.toISOString();
      const toIso = to.toISOString();
      try {
        const grid = await fetchEpg({
          from: fromIso,
          to: toIso,
          timezone: "UTC",
        });
        if (cancelled) return;
        const row = matchEpgChannelRow(
          (grid && grid.channels) || [],
          ch,
          channelKey
        );
        const programs = resolveChannelGuidePrograms(
          (row && row.programs) || [],
          nowPayload
        );
        setSchedule(Array.isArray(programs) ? programs : []);
        setGuideWindow({
          from: (grid && grid.from) || fromIso,
          to: (grid && grid.to) || toIso,
        });
      } catch (e) {
        if (!cancelled) {
          setSchedule(resolveChannelGuidePrograms([], nowPayload));
          setGuideWindow({ from: fromIso, to: toIso });
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [nowPayload, channelKey]);

  const tuned = useMemo(() => {
    if (!nowPayload || nowPayload.success === false) return null;
    return controlPlaneFromChannelNow(nowPayload, {
      siteFeatures: siteFeatures(),
    });
  }, [nowPayload]);

  const streamUrl = useMemo(
    () => resolveChannelStreamUrl(tuned, detail, failoverIndex),
    [tuned, detail, failoverIndex]
  );

  const posterUrl = useMemo(() => {
    const ch = (detail && detail.channel) || (nowPayload && nowPayload.channel) || {};
    return (
      (nowPayload &&
        nowPayload.slate &&
        nowPayload.slate.image &&
        nowPayload.slate.image.url) ||
      ch.banner ||
      (detail && detail.banner) ||
      ""
    );
  }, [detail, nowPayload]);

  const playerConfig = useMemo(() => {
    if (!streamUrl || !tuned) return null;
    const asset = tuned.plane.mediaAsset || {};
    const startTime =
      typeof tuned.joinStartTime === "number" && tuned.joinStartTime > 0
        ? tuned.joinStartTime
        : 0;
    const brand = siteBrand();
    const features = Object.assign({}, tuned.plane.features || {}, {
      watermarks: !!(siteFeatures().watermarks && brand.watermark),
      theaterMode: true,
      fullscreen: true,
    });
    return {
      source: streamUrl,
      contentId: String(asset.id || channelKey),
      title: asset.title || "",
      poster: posterUrl || undefined,
      features: features,
      branding: brand,
      autoplay: true,
      mediaAsset: asset,
      // Continuous join-NOW only (0 for non-continuous / just-started).
      startTime: startTime,
    };
  }, [streamUrl, tuned, channelKey, posterUrl]);

  // Header chrome describes the CHANNEL only — never a video ContentItem /
  // current program / stale airingVideo (those belong in Now Playing).
  const headerMeta = resolveChannelHeaderMeta({
    nowChannel: nowPayload && nowPayload.channel,
    detail: detail,
  });
  const channel = headerMeta.channel;
  const title = headerMeta.title;
  const banner = headerMeta.banner;
  const logo = headerMeta.logo;
  const description = headerMeta.description;
  const related = (detail && detail.related) || [];
  const isFav = favorites.indexOf(String(channel.id || channel.unique_id)) >= 0;
  const loading = nowLoading && !nowPayload && !detail;
  const error = nowError || detailError;

  const plugins = useMemo(() => {
    const list = [createSubtitlesPlugin()];
    if (!tuned || !tuned.avodMode || !tuned.avodMode.enableAvod) {
      return list;
    }
    try {
      list.push(createAvodPlugin({ enabled: true }));
    } catch (e) {
      /* avod optional */
    }
    return list;
  }, [tuned]);

  useEffect(() => {
    setPlayerError(null);
    setFailoverIndex(0);
  }, [streamUrl]);

  const startWatch = () => {
    setPlayerError(null);
    setFailoverIndex(0);
    setWatching(true);
    trackBroadcastEvent({
      event_type: "tune",
      channel_id: channel.id,
      media_asset_key: nowPayload && nowPayload.media_asset && nowPayload.media_asset.id,
      schedule_entry_uid: nowPayload && nowPayload.current && nowPayload.current.unique_id,
    });
    // Existing TPG Player fullscreen — do not create another player.
    window.setTimeout(function () {
      var api = playerApiRef.current;
      if (api && typeof api.enterFullscreen === "function") {
        try {
          api.enterFullscreen();
        } catch (e) {
          /* ignore */
        }
      }
    }, 120);
  };

  const handlePlayerReady = (payload) => {
    playerApiRef.current = (payload && payload.api) || null;
  };

  const handlePlayerError = (err) => {
    const candidates = collectChannelStreamCandidates(tuned, detail);
    if (failoverIndex + 1 < candidates.length) {
      setFailoverIndex(failoverIndex + 1);
      setPlayerError(null);
      return;
    }
    setPlayerError(err);
    refreshNow({ silent: true });
  };

  const handleEnded = () => {
    setPlayerError(null);
    refreshNow({ silent: true });
  };

  const handlePlaybackEvent = (name) => {
    var type = name;
    if (type === "PLAYBACK_STARTED" || type === "playing" || type === "PLAYING") {
      setPlayerError(null);
    }
  };

  const nowTitle =
    (nowPayload && nowPayload.current && nowPayload.current.title) ||
    (detail && detail.now_playing && detail.now_playing.title) ||
    "Off air";
  const nextTitle =
    (nowPayload && nowPayload.next && nowPayload.next.title) ||
    (detail && detail.next_program && detail.next_program.title) ||
    "—";

  const displayProgram =
    selectedProgram ||
    (nowPayload && nowPayload.current) ||
    {};
  const mediaAsset = (nowPayload && nowPayload.media_asset) || {};
  const panelMeta = resolveProgramPanelMeta({
    displayProgram: displayProgram,
    current: nowPayload && nowPayload.current,
    mediaAsset: mediaAsset,
    airingVideo: airingVideo,
    fallbackTitle: nowTitle,
  });
  const isSelectedCurrent = panelMeta.isCurrent;
  const nowDescription = panelMeta.description;
  const panelTitle = panelMeta.title;
  // Only use videos/view enrichment when it matches currently playing media (or selected VOD).
  const matchedAiring =
    isSelectedCurrent && airingVideoMatchesPlayingMedia(airingVideo, mediaAsset)
      ? airingVideo
      : !isSelectedCurrent
        ? airingVideo
        : null;
  const nowRating =
    displayProgram.rating ||
    (matchedAiring && (matchedAiring.age || matchedAiring.rating)) ||
    null;
  const nowStartsAt = displayProgram.starts_at || null;
  const nowEndsAt = displayProgram.ends_at || null;
  const contextualActions = resolveVideoContextualActions(
    isSelectedCurrent
      ? matchedAiring || {
          media_asset: mediaAsset,
        }
      : matchedAiring || displayProgram
  );

  const guideLayout = useMemo(() => {
    const programs = Array.isArray(schedule) ? schedule : [];
    let fromIso = guideWindow && guideWindow.from ? guideWindow.from : null;
    let toIso = guideWindow && guideWindow.to ? guideWindow.to : null;
    if (!fromIso || !toIso) {
      const now = Date.now();
      let start = now - 30 * 60 * 1000;
      let end = now + 5 * 60 * 60 * 1000;
      programs.forEach(function (p) {
        if (p.starts_at) {
          const t = new Date(p.starts_at).getTime();
          if (isFinite(t)) start = Math.min(start, t);
        }
        if (p.ends_at) {
          const t2 = new Date(p.ends_at).getTime();
          if (isFinite(t2)) end = Math.max(end, t2);
        }
      });
      fromIso = new Date(start).toISOString();
      toIso = new Date(end).toISOString();
    }
    const meta = computeGuideTimeline(fromIso, toIso);
    const pxPerMs = 220 / meta.hourMs;
    const widthPx = Math.max(720, Math.round(meta.span * pxPerMs));
    const segments = programs
      .map(function (p, index) {
        const geom = programSegmentGeometry(
          p,
          meta.start,
          meta.end,
          nextProgramStartMs(programs, index)
        );
        if (!geom) {
          return null;
        }
        return {
          program: p,
          left: geom.left,
          width: geom.width,
          isNow: isSameGuideProgram(p, nowPayload && nowPayload.current),
          isSelected: isSameGuideProgram(p, displayProgram),
          isOpenEnded: geom.isOpenEnded,
        };
      })
      .filter(Boolean);
    return {
      start: meta.start,
      end: meta.end,
      span: meta.span,
      hours: meta.hours,
      widthPx: widthPx,
      nowLeft: meta.nowLeft,
      segments: segments,
    };
  }, [schedule, guideWindow, nowPayload, displayProgram]);

  if (notFound) {
    return <ErrorComponent variant="notFound" />;
  }

  return (
    <div className="channel-detail">
      <Helmet>
        <title>{title}</title>
      </Helmet>

      <header className="channel-detail__chrome">
        <div className="channel-detail__chrome-inner">
          {logo ? (
            <img className="channel-detail__logo" src={logo} alt="" />
          ) : null}
          <div className="channel-detail__chrome-copy">
            <p className="channel-detail__eyebrow">
              <span className="channel-detail__live">LIVE</span> Scheduler Channel
            </p>
            <h1>{title}</h1>
            {description ? <p className="channel-detail__desc">{description}</p> : null}
          </div>
          <div className="channel-detail__actions">
            <button type="button" className="channel-detail__cta" onClick={startWatch}>
              Watch Live
            </button>
            <button
              type="button"
              className="channel-detail__ghost"
              onClick={() => {
                const next = toggleFavoriteChannel(channel.id || channel.unique_id);
                setFavorites(next);
              }}
            >
              {isFav ? "Favorited" : "Favorite"}
            </button>
            <Link className="channel-detail__ghost" to="/guide">
              Guide
            </Link>
            <button
              type="button"
              className="channel-detail__ghost"
              onClick={() => {
                const url = window.location.href;
                if (navigator.share) {
                  navigator.share({ title: title, url: url });
                } else if (navigator.clipboard) {
                  navigator.clipboard.writeText(url);
                }
                trackBroadcastEvent({ event_type: "share", channel_id: channel.id });
              }}
            >
              Share
            </button>
          </div>
        </div>
      </header>

      {loading && <p className="channel-detail__status">Loading channel…</p>}
      {error && !notFound && <p className="channel-detail__error">{error}</p>}

      <div className="channel-detail__body">
        <div className="channel-detail__guide">
          <section className="channel-detail__player">
            {watching && tuned ? (
              streamUrl && playerConfig ? (
                <NextGenPlayer
                  key={
                    ((tuned.plane.mediaAsset && tuned.plane.mediaAsset.id) || channelKey) +
                    ":" +
                    streamUrl
                  }
                  config={Object.assign({}, playerConfig, { plugins: plugins })}
                  onReady={handlePlayerReady}
                  onError={handlePlayerError}
                  onEnded={handleEnded}
                  onPlaybackEvent={handlePlaybackEvent}
                />
              ) : (
                <div
                  className="channel-detail__slate"
                  style={{
                    backgroundImage: safeCssBackgroundUrl(banner),
                  }}
                >
                  <p className="channel-detail__live">
                    {((nowPayload && nowPayload.phase) || "slate").toUpperCase()}
                  </p>
                  <h2>{nowTitle}</h2>
                  <p>
                    Standing by for stream — TPG Player is ready when the Media Asset
                    primary URL is available.
                  </p>
                  <button type="button" className="channel-detail__ghost" onClick={() => refreshNow()}>
                    Retry now-playing
                  </button>
                </div>
              )
            ) : (
              <div
                className="channel-detail__slate"
                style={{
                  backgroundImage: safeCssBackgroundUrl(banner || posterUrl),
                }}
              >
                <p className="channel-detail__live">LIVE</p>
                <h2>{nowTitle}</h2>
                <p>{loading ? "Tuning…" : "Connecting to live channel…"}</p>
                {!loading ? (
                  <button type="button" className="channel-detail__cta" onClick={startWatch}>
                    Watch Live
                  </button>
                ) : null}
              </div>
            )}
            {playerError ? (
              <p className="channel-detail__error">
                {(playerError && (playerError.message || playerError.code)) ||
                  "Playback error"}
              </p>
            ) : null}
          </section>

          <aside className="channel-detail__aside">
            <div className="channel-detail__now">
              <p className="channel-detail__label">
                {isSelectedCurrent ? "Now Playing" : "Selected Program"}
              </p>
              <h2>{panelTitle || nowTitle}</h2>
              {(nowStartsAt || nowEndsAt || nowRating) && (
                <p className="channel-detail__now-meta">
                  {formatClock(nowStartsAt)}
                  {nowStartsAt && nowEndsAt ? " – " : ""}
                  {formatClock(nowEndsAt)}
                  {nowRating ? (
                    <span className="channel-detail__now-rating">{nowRating}</span>
                  ) : null}
                </p>
              )}
              {nowDescription ? (
                <p className="channel-detail__now-desc">{nowDescription}</p>
              ) : null}
              {contextualActions.length > 0 ? (
                <div className="channel-detail__now-actions">
                  {contextualActions.map((action) => (
                    <Link
                      key={action.key}
                      className="channel-detail__ghost"
                      to={action.to}
                      aria-label={action.ariaLabel || action.label}
                    >
                      {action.label}
                    </Link>
                  ))}
                </div>
              ) : null}
            </div>
            <div className="channel-detail__next">
              <p className="channel-detail__label">Up Next</p>
              <h3>{nextTitle}</h3>
            </div>
          </aside>
        </div>

        <section className="channel-detail__epg" aria-label="TV Guide">
          <div className="channel-detail__epg-head">
            <h2>TV Guide</h2>
            <p className="channel-detail__epg-channel">{title}</p>
          </div>
          <div className="channel-detail__epg-scroll">
            <div
              className="channel-detail__epg-timeline"
              style={{ width: guideLayout.widthPx + "px" }}
            >
              {guideLayout.nowLeft >= 0 && guideLayout.nowLeft <= 100 ? (
                <>
                  <div
                    className="channel-detail__epg-now-label"
                    style={{ left: guideLayout.nowLeft + "%" }}
                  >
                    NOW
                  </div>
                  <div
                    className="channel-detail__epg-now-line"
                    style={{ left: guideLayout.nowLeft + "%" }}
                    aria-hidden="true"
                  />
                </>
              ) : null}
              <div className="channel-detail__epg-hours">
                {guideLayout.hours.map((t) => (
                  <div
                    key={t}
                    className="channel-detail__epg-hour"
                    style={{
                      left:
                        ((t - guideLayout.start) / guideLayout.span) * 100 +
                        "%",
                    }}
                  >
                    {new Date(t).toLocaleTimeString([], {
                      hour: "numeric",
                    })}
                  </div>
                ))}
              </div>
              <div className="channel-detail__epg-row">
                {guideLayout.segments.map((seg) => (
                  <button
                    type="button"
                    key={programKey(seg.program)}
                    className={
                      "channel-detail__epg-seg" +
                      (seg.isNow ? " is-now" : "") +
                      (seg.isSelected ? " is-selected" : "")
                    }
                    style={{
                      left: seg.left + "%",
                      width: seg.width + "%",
                    }}
                    onClick={() => {
                      userPickedProgramRef.current = true;
                      setSelectedProgram(seg.program);
                    }}
                    title={seg.program.title || "Program"}
                  >
                    <strong>{seg.program.title || seg.program.entry_kind || "Program"}</strong>
                    {isOpenEndedProgram(seg.program) ? null : (
                      <span>
                        {formatClock(seg.program.starts_at)}
                        {seg.program.ends_at
                          ? " – " + formatClock(seg.program.ends_at)
                          : ""}
                      </span>
                    )}
                  </button>
                ))}
                {!guideLayout.segments.length ? (
                  <p className="channel-detail__epg-empty">
                    No programs scheduled for today.
                  </p>
                ) : null}
              </div>
            </div>
          </div>
        </section>

        {related.length > 0 && (
          <section className="channel-detail__related">
            <h2>Related channels</h2>
            <div className="channel-detail__related-grid">
              {related.map((r) => (
                <Link
                  key={r.content_item_id || r.channel_id || r.slug}
                  className="channel-detail__related-card"
                  to={r.detail_route || "/channels/" + (r.slug || r.unique_id)}
                  style={{
                    backgroundImage: safeCssBackgroundUrl(r.banner || r.thumbnail),
                  }}
                >
                  <span>{r.title}</span>
                </Link>
              ))}
            </div>
          </section>
        )}
      </div>
    </div>
  );
}

export default ChannelDetailPage;
